// client/src/intro-exit/Consent.jsx

/**
 * This file renders the structural research agreement documentation screen.
 * It dynamically maps experimental compensation parameters (base pay and maximum performance bounds)
 * from active game treatment properties and validates consent terms before granting entrance.
 */

import React, { useState, useEffect } from "react";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import { getUrlParam } from "../App";

// ==========================================
// 1. Consent Agreement Entry Screen Component
// ==========================================
export function Consent({ next }) {
  const [agreed, setAgreed] = useState(false);
  const game = useGame();
  const player = usePlayer();
  
  // Capture the recruitment-platform identifiers as their own player fields.
  //
  // Expected recruitment link: ?workerId=...&sid=<study>&sesid=<session>
  //
  // Empirica also stores every search param in a single `urlParams` blob, but that
  // is a JSON object and it ignores anything after a "#", so these explicit fields
  // are what analysis should rely on.
  useEffect(() => {
    if (!player) return;

    const studyId = getUrlParam("sid");
    const sessionId = getUrlParam("sesid");
    const workerId = getUrlParam("workerId");

    if (studyId && !player.get("study_id")) player.set("study_id", studyId);
    if (sessionId && !player.get("session_id")) player.set("session_id", sessionId);
    if (workerId && !player.get("worker_id")) player.set("worker_id", workerId);
  }, [player]);

  const basePay = game?.get("treatment")?.basePay !== undefined 
    ? parseFloat(game.get("treatment").basePay) 
    : 5.00; // Safe default fallback

  const bonusFactor = game?.get("treatment")?.correctPredictionBonus !== undefined
    ? parseFloat(game.get("treatment").correctPredictionBonus)
    : 0.25; // Safe default fallback

  const initialPhase = game?.get("treatment")?.initialPhaseRounds ? parseInt(game.get("treatment").initialPhaseRounds, 10) : 4;
  // There is no `postRewireRounds` factor -- the treatment defines the easy and
  // hard counts separately. Reading the non-existent name always fell through to
  // the default of 2, which understated the maximum bonus quoted on the consent
  // form (e.g. $6.00 instead of $7.00 for the 8-player treatment).
  const postRewireEasy = game?.get("treatment")?.postRewireEasyRounds
    ? parseInt(game.get("treatment").postRewireEasyRounds, 10) : 1;
  const postRewireHard = game?.get("treatment")?.postRewireHardRounds
    ? parseInt(game.get("treatment").postRewireHardRounds, 10) : 1;
  const postRewire = postRewireEasy + postRewireHard;
  
  // 2 opportunities per round (initial + final guess) multiplied by the total rounds
  const maxPossibleBonus = (initialPhase + postRewire) * 2 * bonusFactor;

  // ==========================================
  // 2. Main Return Component Render
  // ==========================================
  return (
    <div className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8 font-sans antialiased text-gray-900">
      <div className="max-w-6xl w-full bg-white rounded-2xl shadow-xl border border-gray-200 mx-auto overflow-hidden">
        
        {/* Main Title Header - Matches Page 2 Theme Exactly */}
        <div className="bg-gray-900 px-6 py-4 flex items-center justify-between text-white">
          <div>
            <h1 className="app-page-title">Loan Risk Assessment Experiment</h1>
            <p className="app-page-subtitle text-gray-400 mt-0.5">Please complete the 3 setup documentation steps.</p>
          </div>
          <div className="app-micro-label flex items-center gap-2 font-mono bg-gray-800 px-2 py-1.5 rounded-xl border border-gray-700">
            <span className="text-emerald-400">1. Agreement</span>
            <span className="text-gray-600">&bull;</span>
            <span className="text-gray-500">2. Instructions</span>
            <span className="text-gray-600">&bull;</span>
            <span className="text-gray-500">3. Quiz</span>
          </div>
        </div>

        <div className="p-6 sm:p-7 space-y-3">
          {/* Document Section Subheading */}
          <div className="border-b pb-1">
            <h2 className="app-section-title text-gray-800">Participant Agreement</h2>
            <p className="app-section-subtitle text-gray-500 mt-0.5">
              <strong>Project name:</strong> AI Assistance in Prediction Tasks with Advisors
            </p>
          </div>

          {/* Consent Body Content Container */}
          <div className="app-body space-y-4 text-gray-600 max-h-[55vh] overflow-y-auto pr-4 border-b pb-6 shadow-inner p-4 bg-gray-50/50 rounded-xl border border-gray-100">
            <p>This research is being conducted by Eaman Jahani at the University of Maryland, College Park. The purpose of this research project is to understand how the integration of AI affects professional advisor networks, how trust is established when AI-driven assistants are introduced, and whether human expertise becomes more difficult to differentiate from AI-assisted performance in prediction tasks with advisors.</p>
            <p>The procedures might involve forecasting borrower default risks, reviewing AI predictions, selecting your own network of professional advisors based on their performance, and updating your final risk assessments across multiple trials.</p>
            <p>There is minimal risk from participating in this research study.</p>
            <p>We hope that, in the future, other people might benefit from this study through improved understanding of AI in decision making.</p>
            <p>Any potential loss of confidentiality will be minimized by storing data in a password protected computer managed by researchers from University of Maryland, College Park.</p>
            <p>If we write a report or article about this research project, your identity will be protected to the maximum extent possible. Your information may be shared with representatives of the University of Maryland, College Park or governmental authorities if you or someone else is in danger or if we are required to do so by law.</p>
            <p>You will receive a guaranteed <strong>${basePay.toFixed(2)}</strong> base payment plus a potential <strong>$0.00 - ${maxPossibleBonus.toFixed(2)}</strong> bonus payment based on task performance metrics.</p>
            <p>Your participation in this research is completely voluntary. You may choose not to take part at all. If you decide to participate in this research, you may stop participating at any time. If you decide not to participate in this study or if you stop participating at any time, you will not be penalized; however, you will lose any benefits to which you otherwise qualify.</p>
            <p>If you decide to stop taking part in the study, if you have questions, concerns, or complaints, or if you need to report an injury related to the research, please contact the investigator.</p>
            <p>If you have questions about your rights as a research participant or wish to report a research-related injury, please contact the University of Maryland College Park Institutional Review Board Office.</p>          
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <p className="font-bold text-gray-800 mb-1">Investigator Contact:</p>
                <ul className="list-none space-y-0.5 text-gray-500">
                  <li className="font-medium text-gray-700">Eaman Jahani</li>
                  <li>University of Maryland, College Park</li>
                  <li>
                    <a href="mailto:eaman@umd.edu" className="text-indigo-600 hover:underline font-mono">
                      eaman@umd.edu
                    </a>
                  </li>
                </ul>
              </div>

              <div className="p-3 bg-white rounded-lg border border-gray-200">
                <p className="font-bold text-gray-800 mb-1">University of Maryland College Park Institutional Review Board Office Contact:</p>
                <ul className="list-none space-y-0.5 text-gray-500">
                  <li>1204 Marie Mount Hall, College Park, MD 20742</li>
                  <li>
                    Email:{" "}
                    <a href="mailto:irb@umd.edu" className="text-indigo-600 hover:underline font-mono">
                      irb@umd.edu
                    </a>
                  </li>
                  <li>Tel: 301-405-0678</li>
                </ul>
              </div>
            </div>

            <p className="pt-2">
              For more information regarding participant rights, please visit:{" "}
              <a
                href="https://research.umd.edu/research-resources/research-compliance/institutional-review-board-irb/research-participants"
                target="_blank"
                rel="noreferrer"
                className="text-indigo-600 hover:underline break-all font-mono"
              >
                https://research.umd.edu/research-resources/research-compliance/institutional-review-board-irb/research-participants
              </a>
            </p>

            <p className="app-fine-print text-gray-400">
              This research has been reviewed according to the University of Maryland, College Park IRB procedures for research involving human subjects.
            </p>
          </div>

          {/* Interactive Checkbox Agreement & Action Row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
              />
              <span className="app-question text-gray-700">
                I have read, understood, and agree to the research terms stated above.
              </span>
            </label>

            <button
              type="button"
              disabled={!agreed}
              onClick={next}
              className={`app-btn w-full sm:w-auto px-10 py-2.5 rounded-xl transition shadow-md ${
                agreed
                  ? "bg-gray-900 text-white hover:bg-gray-800 cursor-pointer"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Continue to Instructions
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}